import { RefObject, useEffect, useState } from 'react';

interface UseScrollToElementOptions {
  offsetFromTop?: number;
  scrollBehavior?: ScrollBehavior;
  scrollOnlyOnce?: boolean;
  useWindow?: boolean;
}

/**
 * The `useScrollToElement` function scrolls to a specified element within a container or the window,
 * with optional offset and scroll behavior.
 * @param containerReference - A reference to the container element with the element the user wants to scroll to,
 * @param querySelector - The `querySelector` parameter is a string that represents a CSS,
 * selector. It is used to select the element within the container that the user wants to scroll to,
 * @param options - The `options` parameter is an object that allows you to customize the behavior of
 * the scroll. It has the following properties:
 * @param offsetFromTop - the number of pixels left between the top edges of the screen and the element,
 * @param scrollBehavior - works only with `useWindow` set to `true` and decides whether the scroll effect is smooth or instant,
 * @param scrollOnlyOnce - decides whether the scrolling will be done only once or more times,
 * @param useWindow - decides whether scrolling is performed in the window or in the container.
 */
export const useScrollToElement = <E extends HTMLElement>(
  containerReference: RefObject<E | null> | undefined,
  querySelector: string | undefined,
  options: UseScrollToElementOptions = {},
): void => {
  const { offsetFromTop = 0, scrollBehavior, scrollOnlyOnce = false, useWindow } = options;
  const [shouldScroll, setShouldScroll] = useState(true);

  useEffect(() => {
    const container = containerReference?.current;
    if (!container || !querySelector) {
      return;
    }
    const selectedElement = container.querySelector<HTMLElement>(querySelector);
    if (!selectedElement || !shouldScroll) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const selectedElementOffsetTop = selectedElement.offsetTop - offsetFromTop;
      if (!useWindow) {
        container.scrollTo({ behavior: scrollBehavior, top: selectedElementOffsetTop });
      }
      if (useWindow) {
        window.scrollTo({ behavior: scrollBehavior, top: selectedElementOffsetTop });
      }
      if (scrollOnlyOnce) {
        setShouldScroll(false);
      }
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [
    containerReference,
    offsetFromTop,
    querySelector,
    scrollBehavior,
    scrollOnlyOnce,
    shouldScroll,
    useWindow,
  ]);
};
